'use client'

import React, { useState, useRef } from 'react'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { BedSingle, Camera, MoveLeft, MoveRight, MoveUpRight } from 'lucide-react'
import { Button } from '../ui/button'
import { Property } from '@/payload-types'
import { formatPriceDZD } from '@/utilities/PriceFormat'
import { useTheme } from '@/providers/Theme'

type Props = {
  className?: string
  property: Property[]
}

// Property Card Component
const PropertyCard = ({ property }: { property: Property }) => {
  const { theme } = useTheme()
  return (
    <Link href={`/properties/${property?.slug}`} className="rounded-lg overflow-hidden bg-card border min-h-[330px]">
      {/* Property Image */}
      <div className="relative">
        <div className="aspect-[4/3] w-full relative">
          <img
            src={property?.mediaSection?.images[0]?.image?.url}
            alt={property?.propertyDescription?.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Price Badge */}
        <div className="absolute bottom-3 left-3 bg-white py-1 px-3 rounded-md font-medium">
          {formatPriceDZD(property?.propertyDescription?.price)}{' '}
          {property?.propertyDescription?.afterPriceLabel &&
            ` / ${property?.propertyDescription?.afterPriceLabel}`}
        </div>
      </div>

      {/* Property Info */}
      <div className="p-4">
        <div className="w-full flex justify-between border-b border-b-border pb-2 ">
          <div className="flex flex-col gap-2">
            <h3 className="text-typography text-lg font-medium">
              {property?.propertyDescription?.title}
            </h3>
            <p className="text-sm text-muted mb-2">{property?.locationSection?.address}</p>
          </div>

          {/* Features */}
          <div className="flex flex-col gap-2">
            <div className="text-typography flex items-center gap-2">
              <span className="inline-block">
                <BedSingle size={16} />
              </span>
              <span>{property?.detailSection?.bedrooms}</span>
            </div>
            <div className="text-typography flex items-center gap-2">
              {theme === 'dark' ? (
                <img src="/icons/expand-w.svg" alt="w expand" />
              ) : (
                <img src="/icons/expand.svg" alt="expand" />
              )}
              <span className="text-sm">{property?.detailSection?.surface} m² </span>
            </div>
          </div>
        </div>

        {/* Views & Link */}
        <div className="flex justify-between items-center mt-4 h-[35px] ">
          <div className="h-full flex items-center text-sm text-gray-700 bg-[#F7F7F7] px-2 py-2 rounded-md gap-1">
            <span className="ml-1">{property?.mediaSection?.images?.length}</span>
            <Camera size={16} />
          </div>

          <Button
            asChild
            variant={'outline'}
            className="!h-[35px] !w-[35px] hover:bg-gray-200 transition-colors border !border-border !p-0"
          >
            <div className="!w-[14px] !h-[14px]">
              <img src="/icons/fullscreen.svg" alt="fullscreen" />
            </div>
          </Button>
        </div>
      </div>
    </Link>
  )
}

export const NearbySimilarHomes = ({ className, property }: Props) => {
  const [activeSlide, setActiveSlide] = useState(0)
  const sliderRef = useRef(null)

  // Always show 4 dots and make them functional
  const renderDots = () => {
    // We'll always display 4 dots for visual consistency
    const dots = [
      { index: 0, label: 'First slide' },
      { index: 1, label: 'Second slide' },
      { index: 2, label: 'Third slide' },
      { index: 3, label: 'Fourth slide' },
    ]

    return (
      <>
        {property.map((dot, index) => (
          <div
            key={`dot-${index}`}
            className={`w-2 h-2 rounded-full cursor-pointer ${index === activeSlide ? 'bg-dotsColor' : 'bg-gray-300'}`}
            onClick={() => {
              if (sliderRef.current) {
                sliderRef.current.slickGoTo(index)
                setActiveSlide(index)
              }
            }}
            role="button"
            aria-label={dot.propertyDescription?.title}
          />
        ))}
      </>
    )
  }

  const sliderSettings = {
    dots: false, // Hide default dots
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    beforeChange: (current, next) => {
      // Only update if navigating to a new section
      const newIndex = Math.min(Math.floor(next / 1), 3)
      setActiveSlide(newIndex)
    },
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.5,
          slidesToScroll: 1,
        },
      },
    ],
  }

  return (
    <div className={cn('my-5 container !px-4', className)}>
      <div className="">
        <div className="flex justify-between items-start mb-5">
          <div className="flex flex-col gap-2">
            <h2 className="text-typography md:text-h2 text-mobile-h2 font-semibold">
              Nearby Similar Homes
            </h2>
            <p className="text-sm md:text-base text-muted">
              Aliquam lacinia diam quis lacus euismod
            </p>
          </div>
          {property && property?.length > 3 && (
            <div className="hidden lg:flex space-x-4">
              <button className="text-typography" onClick={() => sliderRef.current?.slickPrev()}>
                <MoveLeft size={20} />
              </button>
              <div className="flex items-center space-x-1">{renderDots(property)}</div>
              <button className="text-typography" onClick={() => sliderRef.current?.slickNext()}>
                <MoveRight size={20} />
              </button>
            </div>
          )}

          <div className="flex md:hidden items-center gap-2">
            <p className="lea text-typography leading-[1.6] text-[15px] font-semibold whitespace-nowrap text-[#181A20]">
              See All Properties
            </p>
            <MoveUpRight size={20} className="text-typography" />
          </div>
        </div>

        {property && property?.length > 3 ? (
          <div className="flex flex-col">
            <Slider {...sliderSettings} className="property-slider" ref={sliderRef}>
              {property &&
                property.map((property) => (
                  <div key={property.id} className="px-2">
                    <PropertyCard property={property} />
                  </div>
                ))}
            </Slider>
            <div className="lg:hidden my-5 flex space-x-4 justify-center">
              <button className="text-typography" onClick={() => sliderRef.current?.slickPrev()}>
                <MoveLeft size={20} />
              </button>
              <div className="flex items-center space-x-1">{renderDots(property)}</div>
              <button className="text-typography" onClick={() => sliderRef.current?.slickNext()}>
                <MoveRight size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {property &&
              property.map((property) => (
                <div key={property.id} className="px-2">
                  <PropertyCard property={property} />
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default NearbySimilarHomes
